import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Set up custom thumbnail gallery
    this.#setupThumbnailGallery();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Sets up the custom thumbnail gallery click handlers and scroll sync.
   *
   * Listeners are delegated to the gallery root and thumbnails are
   * re-queried per event: variant updates and the quick-add modal's morph
   * replace the strip's nodes, which silently detached the previous
   * per-button listeners and IntersectionObserver targets.
   */
  #setupThumbnailGallery() {
    const { signal } = this.#controller;

    this.addEventListener(
      'click',
      (event) => {
        const button =
          event.target instanceof Element ? event.target.closest('[data-thumbnail-index]') : null;
        if (!button) return;
        const index = parseInt(button.dataset.thumbnailIndex ?? '', 10);
        if (Number.isNaN(index)) return;
        this.slideshow?.select(index);
        this.#setActiveThumbnail(index);
      },
      { signal }
    );

    // The slideshow announces every slide change (swipe scroll, arrows,
    // programmatic select) with this bubbling event.
    this.addEventListener(
      'slideshow:select',
      (event) => {
        const index = /** @type {CustomEvent | { detail?: { index?: number } }} */ (event).detail?.index;
        if (typeof index === 'number') this.#setActiveThumbnail(index);
      },
      { signal }
    );
  }

  /**
   * Marks the thumbnail for the given slide index as active and keeps it in
   * view within the strip.
   * @param {number} index - The slide index.
   */
  #setActiveThumbnail(index) {
    const buttons = this.querySelectorAll('[data-thumbnail-index]');
    let activeButton = null;
    for (const button of buttons) {
      const isActive = parseInt(button.dataset.thumbnailIndex ?? '', 10) === index;
      button.classList.toggle('product-thumbnail-gallery__item--active', isActive);
      if (isActive) activeButton = button;
    }
    activeButton?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

